import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import ReactPlayer from "react-player";
import { Modal } from "antd";
import { API_BASE_URL } from "../config/api";
import Navbar from "./Navbar";
import Footer from "./header and footer/Footer";
import Feedback from "./Feedback";
import { markdownToHtml } from "./RichTextEditor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGraduationCap,
  faQuestionCircle,
  faComments,
  faArrowLeft,
  faBookOpen,
  faUserCheck,
  faListCheck,
  faTrophy,
  faRotateRight
} from "@fortawesome/free-solid-svg-icons";

const Course = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const showModal = () => setIsModalOpen(true);
  const handleOk = () => setIsModalOpen(false);
  const handleCancel = () => setIsModalOpen(false);

  const [course, setCourse] = useState({
    course_name: "",
    instructor: "",
    price: null,
    description: "",
    y_link: "",
    p_link: ""
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [duration, setDuration] = useState(0);
  const [played, setPlayed] = useState(0);
  const [lastSyncedPlayed, setLastSyncedPlayed] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const userId = localStorage.getItem("id");

  const navigate = useNavigate();
  const location = useLocation();
  const courseId = location.pathname.split("/")[2];
  const playerRef = useRef(null);

  // Fetch Course details
  useEffect(() => {
    async function fetchCourse() {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/courses/${courseId}`
        );
        setCourse(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching course:", err);
        setError(true);
        setLoading(false);
      }
    }
    fetchCourse();
  }, [courseId]);

  // Check if user has already completed this course (marks >= 90)
  useEffect(() => {
    if (userId && courseId) {
      fetch(`${API_BASE_URL}/api/assessments/user/${userId}/course/${courseId}`)
        .then((res) => res.json())
        .then((data) => {
          if (Array.isArray(data) && data.length > 0) {
            const best = Math.max(...data.map((a) => a.marks || 0));
            if (best >= 90) setIsCompleted(true);
          }
        })
        .catch(() => {});
    }
  }, [userId, courseId]);

  // Fetch Initial Saved Progress from Backend
  useEffect(() => {
    if (userId && courseId) {
      fetch(`${API_BASE_URL}/api/progress/${userId}/${courseId}`)
        .then((res) => res.json())
        .then((data) => {
          if (typeof data === "number" && !isNaN(data)) {
            setPlayed(data);
            setLastSyncedPlayed(data);
          }
        })
        .catch((err) => console.error("Error fetching progress:", err));
    }
  }, [userId, courseId]);

  // Set Duration on Video Load
  const handleDuration = () => {
    if (playerRef.current) {
      const dur = playerRef.current.getDuration();
      if (dur && dur > 0) {
        setDuration(dur);
        fetch(`${API_BASE_URL}/api/progress/update-duration`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, courseId, duration: dur })
        }).catch((err) => console.error("Error updating duration:", err));
      }
    }
  };

  // Real-Time Progress Update Handler on Video Playback
  const handleProgress = (progressState) => {
    const currentPlayedSec = progressState.playedSeconds;
    setPlayed(currentPlayedSec);

    // Persist progress to backend DB every 5 seconds of watch time
    if (currentPlayedSec - lastSyncedPlayed >= 5 && duration > 0) {
      setLastSyncedPlayed(currentPlayedSec);
      fetch(`${API_BASE_URL}/api/progress/update-progress`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          courseId,
          playedTime: currentPlayedSec,
          duration
        })
      }).catch((err) => console.error("Error syncing progress to DB:", err));
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "#f8fafc" }}>
        <Navbar page="courses" />
        <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", color: "#64748b" }}>
          Loading course video module...
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "#f8fafc" }}>
        <Navbar page="courses" />
        <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", color: "#ef4444" }}>
          Could not load course details. Please try again.
        </div>
        <Footer />
      </div>
    );
  }

  const completionPercent = duration > 0 ? Math.min(100, Math.ceil((played / duration) * 100)) : 0;
  const isQuizUnlocked = completionPercent >= 98 || isCompleted;

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "#f8fafc" }}>
      <Navbar page="courses" />

      {/* Header Banner */}
      <div className="page-banner" style={{ padding: "48px 6% 40px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", justifyContent: "center", marginBottom: "14px" }}>
          <button
            onClick={() => navigate("/learnings")}
            style={{
              background: "rgba(255,255,255,0.15)",
              color: "#fff",
              border: "none",
              padding: "6px 16px",
              borderRadius: "20px",
              cursor: "pointer",
              fontSize: "0.88rem",
              fontWeight: 600
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: "6px" }} />
            Back to Dashboard
          </button>
        </div>
        <h1>The Complete {course.course_name || course.courseName} Course</h1>
        <p style={{ maxWidth: "700px", margin: "0 auto" }}>
          Interactive video lecture, hands-on coding exercises, and knowledge assessments.
        </p>
      </div>

      {/* Main Container */}
      <div style={{ padding: "40px 6% 80px", maxWidth: "1200px", margin: "0 auto", width: "100%" }}>

        {/* Full Widescreen Video Player */}
        <div style={{ marginBottom: "32px" }}>
          <div
            style={{
              width: "100%",
              aspectRatio: "16 / 9",
              maxHeight: "540px",
              borderRadius: "20px",
              overflow: "hidden",
              boxShadow: "0 20px 40px -15px rgba(15,23,42,0.3)",
              background: "#000",
              border: "1px solid #1e293b"
            }}
          >
            <ReactPlayer
              ref={playerRef}
              url={course.y_link}
              controls
              width="100%"
              height="100%"
              onDuration={handleDuration}
              onProgress={handleProgress}
            />
          </div>
        </div>

        {/* ── Unified Course Info + Progress Card ── */}
        <div
          style={{
            background: "#fff",
            borderRadius: "20px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 8px 24px -6px rgba(0,0,0,0.07)",
            overflow: "hidden",
            marginBottom: "32px"
          }}
        >
          {/* Progress Banner Strip */}
          <div
            style={{
              background: "linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)",
              padding: "20px 28px",
              display: "flex",
              alignItems: "center",
              gap: "20px",
              flexWrap: "wrap"
            }}
          >
            <div style={{ flex: 1, minWidth: "200px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <span style={{ fontWeight: 700, color: "rgba(255,255,255,0.9)", fontSize: "0.9rem", letterSpacing: "0.03em" }}>
                  VIDEO PROGRESS
                </span>
                <span style={{ fontWeight: 900, color: "#fff", fontSize: "1.15rem" }}>{completionPercent}%</span>
              </div>
              <div style={{ background: "rgba(255,255,255,0.2)", borderRadius: "8px", height: "8px", overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    borderRadius: "8px",
                    width: `${completionPercent}%`,
                    background: completionPercent >= 98 ? "#10b981" : "#a5b4fc",
                    transition: "width 0.4s ease"
                  }}
                />
              </div>
              <p style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.75)", margin: "6px 0 0" }}>
                {completionPercent >= 98
                  ? "🎉 100% complete — quiz unlocked!"
                  : `Watch ${100 - completionPercent}% more to unlock the assessment quiz.`}
              </p>
            </div>

            {/* Completion Badge */}
            {isCompleted && (
              <div
                style={{
                  background: "rgba(16,185,129,0.2)",
                  border: "1.5px solid rgba(16,185,129,0.5)",
                  borderRadius: "12px",
                  padding: "10px 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexShrink: 0
                }}
              >
                <FontAwesomeIcon icon={faTrophy} style={{ color: "#10b981", fontSize: "1.2rem" }} />
                <div>
                  <div style={{ color: "#10b981", fontWeight: 800, fontSize: "0.85rem" }}>Course Completed</div>
                  <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}>Score ≥ 90%</div>
                </div>
              </div>
            )}
          </div>

          {/* Body — Description + Meta + Actions */}
          <div style={{ padding: "28px", display: "grid", gridTemplateColumns: "1fr auto", gap: "28px", alignItems: "start" }}>

            {/* Left: Description + meta */}
            <div>
              <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.25rem", fontWeight: 800, color: "#0f172a", marginBottom: "10px" }}>
                {course.course_name || course.courseName}
              </h3>
              {course.description ? (
                <div
                  className="course-full-description"
                  style={{ color: "#475569", fontSize: "0.95rem", lineHeight: "1.75", marginBottom: "20px" }}
                  dangerouslySetInnerHTML={{ __html: markdownToHtml(course.description) }}
                />
              ) : (
                <p style={{ color: "#475569", fontSize: "0.95rem", lineHeight: "1.75", marginBottom: "20px" }}>
                  {`This online programming course provides a comprehensive introduction to ${
                    course.course_name || course.courseName
                  }. Whether you are a beginner or looking to expand your coding skills, this course covers fundamental concepts and prepares you for real-world projects.`}
                </p>
              )}

              {/* Meta info chips */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                <div
                  style={{
                    display: "flex", alignItems: "center", gap: "8px",
                    background: "#f1f5f9", borderRadius: "30px", padding: "7px 16px",
                    fontSize: "0.85rem", color: "#334155", fontWeight: 600
                  }}
                >
                  <FontAwesomeIcon icon={faUserCheck} style={{ color: "#4f46e5" }} />
                  {course.instructor || course.tutor || "Academic Specialist"}
                </div>
                <div
                  style={{
                    display: "flex", alignItems: "center", gap: "8px",
                    background: "#f1f5f9", borderRadius: "30px", padding: "7px 16px",
                    fontSize: "0.85rem", color: "#334155", fontWeight: 600
                  }}
                >
                  <FontAwesomeIcon icon={faBookOpen} style={{ color: "#06b6d4" }} />
                  Interactive Video
                </div>
                <div
                  style={{
                    display: "flex", alignItems: "center", gap: "8px",
                    background: "#f1f5f9", borderRadius: "30px", padding: "7px 16px",
                    fontSize: "0.85rem", color: "#334155", fontWeight: 600
                  }}
                >
                  <FontAwesomeIcon icon={faGraduationCap} style={{ color: "#f59e0b" }} />
                  Self-Paced
                </div>
                <div
                  style={{
                    display: "flex", alignItems: "center", gap: "8px",
                    background: "#f1f5f9", borderRadius: "30px", padding: "7px 16px",
                    fontSize: "0.85rem", color: "#334155", fontWeight: 600
                  }}
                >
                  <FontAwesomeIcon icon={faListCheck} style={{ color: "#10b981" }} />
                  Quiz Included
                </div>
              </div>
            </div>

            {/* Right: Action Buttons */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", minWidth: "200px" }}>
              {isQuizUnlocked ? (
                <button
                  className="enroll-button"
                  style={{
                    background: isCompleted
                      ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                      : "linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)",
                    color: "#fff",
                    width: "100%",
                    padding: "13px 20px",
                    fontSize: "0.92rem",
                    borderRadius: "12px",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: 700,
                    boxShadow: isCompleted
                      ? "0 4px 14px rgba(16,185,129,0.35)"
                      : "0 4px 14px rgba(79,70,229,0.35)",
                    transition: "all 0.25s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px"
                  }}
                  onClick={() => navigate(`/assessment/${courseId}`)}
                >
                  <FontAwesomeIcon icon={isCompleted ? faRotateRight : faQuestionCircle} />
                  {isCompleted ? "Retake Quiz" : "Take Assessment Quiz"}
                </button>
              ) : (
                <button
                  className="enroll-button-deactive"
                  style={{ width: "100%", padding: "13px 20px", fontSize: "0.92rem" }}
                  onClick={showModal}
                >
                  <FontAwesomeIcon icon={faQuestionCircle} style={{ marginRight: "8px" }} />
                  Quiz Locked (Reach 100%)
                </button>
              )}

              <button
                onClick={() => navigate(`/discussion/${courseId}`)}
                style={{
                  background: "#f8fafc",
                  color: "#334155",
                  border: "1.5px solid #e2e8f0",
                  padding: "13px 20px",
                  borderRadius: "12px",
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  cursor: "pointer",
                  transition: "all 0.25s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px"
                }}
              >
                <FontAwesomeIcon icon={faComments} style={{ color: "#4f46e5" }} />
                Class Discussion
              </button>
            </div>
          </div>
        </div>

        {/* Feedback Component */}
        <Feedback courseid={courseId} />
      </div>

      {/* Quiz Locked Modal */}
      <Modal
        title="Assessment Quiz Locked"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <p style={{ color: "#334155", fontSize: "0.95rem", lineHeight: "1.6" }}>
          Please watch 100% of the video course module to unlock the assessment quiz and test your knowledge.
        </p>
      </Modal>

      <Footer />
    </div>
  );
};

export default Course;
